import React, { useEffect, useMemo, useRef, useState } from "react";
import { ENGINES, QUICK_PHRASES, RoomSnapshot } from "@samra/shared";
import { get, joinRoomChannel, leaveRoomChannel, newKey, patch, post, send, subscribe } from "../api";
import { errText, getLang, t } from "../i18n";
import { navigate, refreshWallet, toast, useStore } from "../store";
import { Avatar, Button, Coins, Empty, GiftIcon, Link, MicIcon, Modal, Spinner, cx, useAsync } from "../ui";
import { Board } from "../games";
import { VoicePanel } from "../voicepanel";
import { leaveVoiceFor, useVoice } from "../voice";
import { playEvents, setSound, sfx, useSoundOn } from "../sound";

interface GameState {
  matchId: string;
  seat: number;
  view: any;
  moveNo: number;
  deadline: number | null;
}

export interface BoardProps {
  view: any;
  seat: number;
  names: string[];
  myTurn: boolean;
  move: (m: unknown) => void;
  events: { list: any[]; seq: number };
  /** A move was sent and the server has not answered yet: boards must not accept another one. */
  busy?: boolean;
  /** Live connection per game seat (true = connected). */
  connected?: boolean[];
}

export function Room({ roomId }: { roomId: string }) {
  const me = useStore((s) => s.me)!;
  const [room, setRoom] = useState<RoomSnapshot | null>(null);
  const [game, setGame] = useState<GameState | null>(null);
  const [events, setEvents] = useState<{ list: any[]; seq: number }>({ list: [], seq: 0 });
  const [over, setOver] = useState<any>(null);
  const [chat, setChat] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [giftFor, setGiftFor] = useState<{ id: string; name: string } | null>(null);
  const [inviting, setInviting] = useState(false);
  const [tab, setTab] = useState<"players" | "chat">("players");
  const sideRef = useRef<HTMLElement>(null);
  const openChat = () => {
    setTab("chat");
    setUnreadChat(0);
    if (window.matchMedia("(max-width: 900px)").matches) {
      // On phones the panel sits below the board: bring it to the top of the screen so the composer is in reach.
      requestAnimationFrame(() => sideRef.current?.scrollIntoView({ block: "start" }));
    }
  };
  const [unreadChat, setUnreadChat] = useState(0);
  // The move number we sent a move for. Cleared by the next game state, by an error, or by a timeout.
  const [pending, setPending] = useState<number | null>(null);
  const pendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPending = () => {
    if (pendingTimer.current) clearTimeout(pendingTimer.current);
    pendingTimer.current = null;
    setPending(null);
  };
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const gameIdRef = useRef<string | null>(null);
  gameIdRef.current = room?.gameId ?? null;
  const soundOn = useSoundOn();

  useEffect(() => {
    const off = subscribe((m) => {
      if (m.t === "room.state" && m.room.id === roomId) {
        setRoom(m.room);
        if (m.room.status === "waiting" && !m.room.matchId) setGame((g) => (g && !g.view.over ? null : g));
      }
      if (m.t === "game.state" && m.roomId === roomId) {
        clearPending();
        setGame((g) => (g && g.matchId === m.matchId && g.moveNo > m.moveNo ? g : { matchId: m.matchId, seat: m.seat, view: m.view, moveNo: m.moveNo, deadline: m.deadline }));
        setOver((o: any) => (o && o.matchId !== m.matchId ? null : o));
      }
      if (m.t === "game.events" && m.roomId === roomId) {
        setEvents((e) => ({ list: m.events, seq: e.seq + 1 }));
        if (gameIdRef.current) playEvents(gameIdRef.current, m.events);
      }
      if (m.t === "game.over" && m.roomId === roomId) {
        setOver(m.result);
        void refreshWallet();
      }
      if (m.t === "chat.msg" && m.roomId === roomId) {
        setChat((c) => (c.some((x) => x.id === m.msg.id) ? c : [...c.slice(-99), m.msg]));
        if (tabRef.current !== "chat") setUnreadChat((n) => n + 1);
      }
      if (m.t === "room.removed" && m.roomId === roomId) {
        toast(t.voiceRemoved, "error");
        leaveVoiceFor(roomId);
        navigate("/");
      }
      if (m.t === "error" && (m.ref === roomId)) setError(errText(m));
      if (m.t === "error" && m.ref === "chat") toast(errText(m), "error");
      // stale/out-of-turn moves just mean the board changed under the click; the server resyncs us
      if (m.t === "error" && m.ref === "game.move") {
        clearPending();
        if (!["stale_move", "not_your_turn"].includes(m.code)) toast(errText(m), "error");
      }
    });
    joinRoomChannel(roomId);
    return () => {
      off();
      if (pendingTimer.current) clearTimeout(pendingTimer.current);
      leaveVoiceFor(roomId);
      leaveRoomChannel(roomId);
    };
  }, [roomId]);

  const seats = room?.seats ?? [];
  const isHost = room?.hostId === me.id;
  const modAction = (path: string, body: object) => post(path, body).catch((e) => toast(errText(e), "error"));
  const mySeat = seats.find((s) => s.userId === me.id);
  const engine = room ? ENGINES[room.gameId] : null;
  const names = useMemo(() => seats.map((s) => (s.userId === me.id ? t.you : s.username)), [seats, me.id]);

  // A short chime when the turn comes to you (not for the extra roll you keep after a six).
  const turnNow = !!game && !game.view.over && room?.status === "playing" && game.seat === game.view.turn;
  const hadTurn = useRef(false);
  useEffect(() => {
    if (turnNow && !hadTurn.current) setTimeout(sfx.turn, 350);
    hadTurn.current = turnNow;
  }, [turnNow]);

  if (error && !room) return <Empty>{error} <Link to="/">{t.home}</Link></Empty>;
  if (!room || !engine) return <Spinner />;

  const playing = !!game && !game.view.over && room.status === "playing";
  const turnSeat = game ? game.view.turn : -1;
  const myTurn = playing && game!.seat === turnSeat;

  // One move at a time: a double tap, or a tap while the last move is in flight, sends nothing.
  // The server stays the judge (it refuses stale or out-of-turn moves); this only stops duplicates.
  const busy = pending !== null && !!game && pending === game.moveNo;
  const move = (m: unknown) => {
    if (!game || busy) return;
    setPending(game.moveNo);
    if (pendingTimer.current) clearTimeout(pendingTimer.current);
    pendingTimer.current = setTimeout(() => {
      pendingTimer.current = null;
      setPending(null);
      toast(t.moveTimeout, "error");
    }, 8000);
    send({ t: "game.move", roomId, move: m, moveNo: game.moveNo });
  };

  const leave = async () => {
    leaveVoiceFor(roomId);
    await post(`/api/rooms/${roomId}/leave`).catch(() => {});
    navigate(`/games/${room.gameId}`);
  };

  return (
    <div className={cx("room", `room-${room.gameId}`)}>
      <div className="room-stage">
        <header className="room-bar">
          <button className="icon-btn" onClick={leave} aria-label={t.leave}>
            <svg viewBox="0 0 24 24" width="22" height="22"><path d="M14 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" fill="none" className="flip-rtl" /></svg>
          </button>
          <h1>{t.game[room.gameId]}</h1>
          {playing && <TurnClock deadline={game!.deadline} label={myTurn ? t.yourTurn : t.turnOf(names[turnSeat] ?? "")} mine={myTurn} />}
          {playing && room.visibility === "private" && <CodeChip code={room.code} />}
          <button className={cx("icon-btn", "sound-btn", !soundOn && "is-off")} onClick={() => setSound(!soundOn)} aria-pressed={soundOn} aria-label={soundOn ? t.soundOff : t.soundOn} title={soundOn ? t.soundOff : t.soundOn}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor" fillOpacity="0.15" />
              {soundOn ? <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" /> : <path d="M16 9l5 6M21 9l-5 6" />}
            </svg>
          </button>
        </header>
        {game && (room.status === "playing" || over) ? (
          <div className="board-wrap">
            <Board gameId={room.gameId} view={game.view} seat={game.seat} names={names} myTurn={myTurn} move={move} events={events} busy={busy} connected={seats.map((s) => s.connected)} />
          </div>
        ) : (
          <WaitingRoom room={room} engine={engine} isHost={isHost} meReady={!!mySeat?.ready} onInvite={() => setInviting(true)} />
        )}
        {over && <Results over={over} names={names} room={room} isHost={isHost} onClose={() => { setOver(null); setGame(null); }} />}
      </div>
      <aside className="room-side" ref={sideRef}>
        <VoicePanel roomId={roomId} meId={me.id} hostId={room.hostId} />
        <div className="tabs" role="tablist">
          <button role="tab" aria-selected={tab === "players"} onClick={() => setTab("players")}>{t.players}</button>
          <button role="tab" aria-selected={tab === "chat"} onClick={openChat}>
            {t.chat} {unreadChat > 0 && <span className="badge">{unreadChat}</span>}
          </button>
        </div>
        {tab === "players" ? (
          <>
          {isHost && room.status === "waiting" && <ModSettings room={room} />}
          <ul className="seats">
            {seats.map((s, i) => (
              <li key={s.userId} className={cx("seat", `seat-c${i}`, turnSeat === i && playing && "is-turn")}>
                <Avatar name={s.username} url={s.avatar} online={s.connected} size={40} />
                <span className="seat-name">
                  <strong>
                    {s.userId === me.id ? t.you : s.username}
                    {s.userId === room.hostId && <span className="mod-badge" title={t.voiceBadge}>{t.host}</span>}
                  </strong>
                  <small>{s.userId === room.hostId ? "" : room.status === "waiting" ? (s.ready ? t.ready : t.notReady) : ""}{s.voiceMuted ? ` · ${t.voiceMutedBadge}` : ""}</small>
                </span>
                {isHost && s.userId !== me.id && room.status === "waiting" && (
                  <span className="seat-actions mod-actions">
                    <button className="btn-small" onClick={() => modAction(`/api/rooms/${room.id}/kick`, { userId: s.userId })}>{t.kickPlayer}</button>
                    <button className="btn-small" onClick={() => modAction(`/api/rooms/${room.id}/transfer`, { userId: s.userId })}>{t.transferMod}</button>
                    <button className="btn-small" onClick={() => modAction(`/api/rooms/${room.id}/voice-mute`, { userId: s.userId, muted: !s.voiceMuted })}>{s.voiceMuted ? t.voiceUnmuteP : t.voiceMuteP}</button>
                  </span>
                )}
                {s.userId !== me.id && (
                  <span className="seat-actions">
                    <button className="icon-btn" title={t.sendGift} aria-label={t.sendGift} onClick={() => setGiftFor({ id: s.userId, name: s.username })}>
                      <svg viewBox="0 0 24 24" width="20" height="20"><path d="M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7c-2-4-6-4-6-1s6 1 6 1 6 2 6-1-4-3-6 1" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinejoin="round" /></svg>
                    </button>
                    <PlayerMenu userId={s.userId} name={s.username} roomId={roomId} />
                  </span>
                )}
              </li>
            ))}
            {Array.from({ length: Math.max(0, room.maxPlayers - seats.length) }).map((_, i) => (
              <li key={"e" + i} className="seat seat-empty">{t.emptySeat}</li>
            ))}
          </ul>
          </>
        ) : (
          <Chat roomId={roomId} messages={chat} meId={me.id} />
        )}
      </aside>
      {giftFor && <GiftModal to={giftFor} roomId={roomId} onClose={() => setGiftFor(null)} />}
      {inviting && <InviteModal roomId={roomId} onClose={() => setInviting(false)} />}
    </div>
  );
}

function TurnClock({ deadline, label, mine }: { deadline: number | null; label: string; mine: boolean }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);
  const left = deadline ? Math.max(0, Math.ceil((deadline - now) / 1000)) : null;
  return (
    <span className={cx("turn-clock", mine && "is-mine", left !== null && left <= 5 && "is-low")} role="timer">
      <bdi>{label}</bdi>
      {left !== null && <span className="tc-sec" aria-label={t.secondsLeft(left)}><bdi dir="ltr">{left}</bdi></span>}
    </span>
  );
}

function CodeChip({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="code-chip"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(code);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {}
      }}
    >
      <bdi dir="ltr">{code}</bdi> <small>{copied ? t.copied : t.copy}</small>
    </button>
  );
}

function WaitingRoom({ room, engine, isHost, meReady, onInvite }: { room: RoomSnapshot; engine: any; isHost: boolean; meReady: boolean; onInvite: () => void }) {
  const [busy, setBusy] = useState(false);
  const voice = useVoice();
  const enough = engine.playerCounts.includes(room.seats.length);
  const allReady = room.seats.every((s) => s.ready || s.userId === room.hostId);
  const voiceHere = voice.roomId === room.id;
  return (
    <div className="waiting">
      <div className="waiting-top">
        <span className="waiting-count" dir="ltr">{room.seats.length}/{room.maxPlayers}</span>
        <span className="waiting-label">{t.players}</span>
      </div>
      <div className="waiting-seats">
        {Array.from({ length: room.maxPlayers }).map((_, i) => {
          const s = room.seats[i];
          if (!s) {
            return (
              <div key={i} className="waiting-seat is-empty">
                <span className="seat-hole" />
                <span className="wr-name muted">{t.emptySeat}</span>
              </div>
            );
          }
          const vp = voiceHere ? voice.participants.find((p) => p.userId === s.userId) : undefined;
          const mic = !vp ? "off" : vp.serverMuted ? "server" : vp.muted ? "off" : "on";
          const micLabel = mic === "server" ? t.voiceMutedByMod : mic === "on" ? t.voiceMicOpen : t.voiceMicClosed;
          const isHostSeat = s.userId === room.hostId;
          return (
            <div key={i} className={cx("waiting-seat", "is-taken", `seat-c${i}`, s.ready && "is-ready", isHostSeat && "is-host")}>
              <div className="wr-avatar">
                <Avatar name={s.username} url={s.avatar} size={72} online={s.connected} />
                <span className={cx("wr-mic", `mic-${mic}`)} title={micLabel} aria-label={micLabel}>
                  <MicIcon state={mic} />
                </span>
              </div>
              <span className="wr-name">{s.username}</span>
              <span className="wr-tags">
                {isHostSeat ? (
                  <span className="mod-badge" title={t.voiceBadge}>{t.host}</span>
                ) : (
                  <span className={cx("wr-ready", s.ready && "is-ready")}>{s.ready ? t.ready : t.notReady}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {room.visibility === "private" && (
        <p className="share">
          {t.shareCode}: <CodeChip code={room.code} />
        </p>
      )}
      <div className="row center">
        <Button kind="ghost" onClick={onInvite}>{t.inviteFriends}</Button>
        {isHost ? (
          <Button
            busy={busy}
            disabled={!enough || !allReady}
            onClick={async () => {
              setBusy(true);
              try {
                await post(`/api/rooms/${room.id}/start`);
              } catch (e) {
                toast(errText(e), "error");
              } finally {
                setBusy(false);
              }
            }}
          >
            {t.start}
          </Button>
        ) : (
          <Button kind={meReady ? "ghost" : "primary"} onClick={() => post(`/api/rooms/${room.id}/ready`, { ready: !meReady }).catch((e) => toast(errText(e), "error"))}>
            {meReady ? t.notReady : t.imReady}
          </Button>
        )}
      </div>
      <p className="muted center">
        {!enough ? t.needPlayers(engine.playerCounts.join(" / ")) : !isHost ? t.waitingHost : !allReady ? t.errors.players_not_ready : ""}
      </p>
    </div>
  );
}

function Results({ over, names, room, isHost, onClose }: { over: any; names: string[]; room: RoomSnapshot; isHost: boolean; onClose: () => void }) {
  const me = useStore((s) => s.me)!;
  const winners: number[] = over.ranking[0] ?? [];
  const award = (seat: number) => over.awards?.find((a: any) => a.userId === over.seats.find((s: any) => s.seat === seat)?.userId);
  return (
    <div className="results" role="dialog" aria-label={t.gameOver}>
      <div className="results-card">
        <h2>{t.gameOver}</h2>
        <p className="results-win">
          {winners.length > 1 ? t.winners : t.winner}: <strong>{winners.map((s) => names[s]).join(" + ")}</strong>
        </p>
        <ol className="results-list">
          {over.ranking.map((group: number[], i: number) =>
            group.map((s) => (
              <li key={s} className={cx(`seat-c${s}`, over.seats.find((x: any) => x.seat === s)?.userId === me.id && "is-me")}>
                <span>{t.place(i + 1)}</span>
                <strong>{names[s]}</strong>
                <small>{over.scores?.[s] !== undefined ? `${over.scores[s]} ${t.points}` : ""}</small>
                {award(s) && <em>{t.xpEarned(award(s).xp)}</em>}
              </li>
            )),
          )}
        </ol>
        <div className="row center">
          <Button kind="ghost" onClick={() => navigate(`/games/${room.gameId}`)}>{t.backToLobby}</Button>
          <Button onClick={onClose}>{isHost ? t.rematch : t.close}</Button>
        </div>
      </div>
    </div>
  );
}

/** Moderator-only room settings. The server enforces them too; they can only change while the room is waiting. */
function ModSettings({ room }: { room: RoomSnapshot }) {
  const counts = (ENGINES[room.gameId]?.playerCounts ?? [room.maxPlayers]).filter((n) => n >= room.seats.length);
  const [saving, setSaving] = useState(false);
  const save = async (body: { visibility?: "public" | "private"; maxPlayers?: number }) => {
    setSaving(true);
    try {
      await patch(`/api/rooms/${room.id}/settings`, body);
      toast(t.roomSettingsSaved);
    } catch (e) {
      toast(errText(e), "error");
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="mod-settings" aria-label={t.roomSettings}>
      <h2 className="mod-settings-title">{t.roomSettings}</h2>
      <div className="mod-settings-row">
        <span id="ms-vis">{t.roomVisibility}</span>
        <div className="segmented" role="group" aria-labelledby="ms-vis">
          {(["public", "private"] as const).map((v) => (
            <button key={v} type="button" aria-pressed={room.visibility === v} disabled={saving} onClick={() => room.visibility !== v && save({ visibility: v })}>
              {v === "public" ? t.public : t.private}
            </button>
          ))}
        </div>
      </div>
      <label className="mod-settings-row">
        <span>{t.roomSeats}</span>
        <select value={room.maxPlayers} disabled={saving} dir="ltr" onChange={(e) => save({ maxPlayers: Number(e.target.value) })}>
          {counts.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </label>
    </section>
  );
}

function Chat({ roomId, messages, meId }: { roomId: string; messages: any[]; meId: string }) {
  const [text, setText] = useState("");
  const log = useRef<HTMLDivElement>(null);
  const form = useRef<HTMLFormElement>(null);
  // Scroll the message list itself. scrollIntoView would also scroll the page and push the composer away.
  useEffect(() => {
    if (log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [messages.length]);
  // When the on-screen keyboard opens, keep the composer (not only the input) above it.
  const onFocus = () =>
    setTimeout(() => form.current?.scrollIntoView({ block: "nearest" }), 350);
  const render = (m: any) => {
    const p = /^:([a-z]+):$/.exec(m.text);
    return p && QUICK_PHRASES[p[1]] ? <em>{QUICK_PHRASES[p[1]][getLang()]}</em> : m.text;
  };
  return (
    <div className="chat">
      <div className="chat-log" ref={log} aria-live="polite">
        {messages.map((m) => (
          <div key={m.id} className={cx("msg", m.userId === meId && "is-me")}>
            <b>{m.userId === meId ? t.you : m.username}</b> <span>{render(m)}</span>
          </div>
        ))}
      </div>
      <div className="phrases">
        {Object.keys(QUICK_PHRASES).map((k) => (
          <button key={k} onClick={() => send({ t: "chat.send", roomId, phrase: k })}>{QUICK_PHRASES[k][getLang()]}</button>
        ))}
      </div>
      <form
        ref={form}
        className="chat-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!text.trim()) return;
          send({ t: "chat.send", roomId, text: text.trim() });
          setText("");
        }}
      >
        <input value={text} onChange={(e) => setText(e.target.value)} onFocus={onFocus} maxLength={300} placeholder={t.typeMessage} aria-label={t.typeMessage} enterKeyHint="send" autoComplete="off" />
        <Button type="submit" kind="ghost">{t.sendMsg}</Button>
      </form>
    </div>
  );
}

function PlayerMenu({ userId, name, roomId }: { userId: string; name: string; roomId: string }) {
  const [open, setOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [muted, setMuted] = useState(false);
  return (
    <>
      <button className="icon-btn" aria-label="…" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <svg viewBox="0 0 24 24" width="20" height="20"><circle cx="5" cy="12" r="1.8" fill="currentColor" /><circle cx="12" cy="12" r="1.8" fill="currentColor" /><circle cx="19" cy="12" r="1.8" fill="currentColor" /></svg>
      </button>
      {open && (
        <div className="menu" onMouseLeave={() => setOpen(false)}>
          <button onClick={async () => { await post("/api/friends/request", { userId }).then(() => toast(t.requestSent), (e) => toast(errText(e), "error")); setOpen(false); }}>{t.addFriend}</button>
          <button onClick={async () => { await post("/api/mutes", { userId, mute: !muted }); setMuted(!muted); setOpen(false); }}>{muted ? t.unmute : t.mute}</button>
          <button onClick={() => { setReporting(true); setOpen(false); }}>{t.report}</button>
          <button className="danger" onClick={async () => { await post("/api/blocks", { userId }); toast(t.block); setOpen(false); }}>{t.block}</button>
        </div>
      )}
      {reporting && <ReportModal userId={userId} name={name} roomId={roomId} onClose={() => setReporting(false)} />}
    </>
  );
}

export function ReportModal({ userId, name, roomId, dmMessageId, onClose }: { userId: string; name: string; roomId?: string; dmMessageId?: string; onClose: () => void }) {
  const [reason, setReason] = useState("abuse");
  const [details, setDetails] = useState("");
  return (
    <Modal title={`${t.report}: ${name}`} onClose={onClose}>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await post("/api/reports", { userId, reason, details: details || undefined, roomId, dmMessageId });
            toast(t.reportSent);
            onClose();
          } catch (e) {
            toast(errText(e), "error");
          }
        }}
      >
        <select value={reason} onChange={(e) => setReason(e.target.value)} aria-label={t.reportReason}>
          {Object.entries(t.reasons).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <textarea value={details} onChange={(e) => setDetails(e.target.value)} maxLength={500} rows={3} />
        <Button type="submit">{t.report}</Button>
      </form>
    </Modal>
  );
}

export function GiftModal({ to, roomId, onClose }: { to: { id: string; name: string }; roomId?: string; onClose: () => void }) {
  const gifts = useAsync(() => get("/api/store/gifts"));
  const balance = useStore((s) => s.balance);
  const [pick, setPick] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());
  const sendable = (gifts.data?.gifts ?? []).filter((g: any) => !["table_skin", "effect"].includes(g.category));
  return (
    <Modal title={t.giftTo(to.name)} onClose={onClose} wide>
      {gifts.loading ? (
        <Spinner />
      ) : (
        <>
          <div className="gift-grid">
            {sendable.map((g: any) => (
              <button key={g.id} className={cx("gift-card", pick?.id === g.id && "is-on", `cat-${g.category}`)} onClick={() => { setPick(g); key.current = newKey(); }}>
                <GiftIcon icon={g.icon} />
                <span>{getLang() === "ar" ? g.name_ar : g.name_en}</span>
                <Coins n={g.price_coins} />
              </button>
            ))}
          </div>
          <footer className="gift-foot">
            <span>{t.balance}: <Coins n={balance?.total ?? 0} /></span>
            {pick && (balance?.total ?? 0) < pick.price_coins ? (
              <Link to="/store" className="btn btn-primary">{t.topUp}</Link>
            ) : (
              <Button
                disabled={!pick}
                busy={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await post("/api/gifts/send", { giftId: pick.id, recipientId: to.id, expectedPrice: pick.price_coins, idempotencyKey: key.current, roomId });
                    toast(t.giftSent);
                    await refreshWallet();
                    onClose();
                  } catch (e) {
                    toast(errText(e), "error");
                    if ((e as any).code === "price_changed") gifts.reload();
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {pick ? t.confirmSend(pick.price_coins) : t.sendGift}
              </Button>
            )}
          </footer>
        </>
      )}
    </Modal>
  );
}

function InviteModal({ roomId, onClose }: { roomId: string; onClose: () => void }) {
  const friends = useAsync(() => get("/api/friends"));
  const [sent, setSent] = useState<Set<string>>(new Set());
  return (
    <Modal title={t.inviteFriends} onClose={onClose}>
      {friends.loading ? (
        <Spinner />
      ) : friends.data?.friends?.length ? (
        <ul className="list">
          {friends.data.friends.map((f: any) => (
            <li key={f.id} className="list-row">
              <Avatar name={f.displayName} url={f.avatarUrl} online={f.online} size={36} />
              <span className="grow">{f.displayName}</span>
              <Button
                kind="ghost"
                disabled={sent.has(f.id)}
                onClick={async () => {
                  try {
                    await post(`/api/rooms/${roomId}/invite`, { userId: f.id });
                    setSent(new Set([...sent, f.id]));
                  } catch (e) {
                    toast(errText(e), "error");
                  }
                }}
              >
                {sent.has(f.id) ? t.sent : t.invite}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <Empty>{t.noFriends}</Empty>
      )}
    </Modal>
  );
}
